import * as React from 'react';
import type { ReactNode } from 'react';
import { Button } from '@ooh/ui/components/button';
import { FormField } from '@ooh/ui/components/form-field';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { SearchableSelectField } from '@ooh/ui/components/searchable-select-field';
import { OfferShareSchema } from '@ooh/contracts';
import type { OfferShareRecord, ShareableRole, ShareableUser } from '@ooh/contracts';
import { useTranslation } from 'react-i18next';
import { toast } from '@ooh/ui/components/sonner';
import { QueryBoundary } from '../../layouts/query-boundary';
import { FormSkeleton } from '../../layouts/skeletons';
import { useOfferSharingData, useUpdateOfferShares } from './api';

interface SharingDialogProps {
  isOpen: boolean;
  offerId: string;
  onClose: () => void;
}

const SHARING_DIALOG_MAX_WIDTH = 'max-w-2xl';

interface GrantFormState {
  permission: SharePermission;
  value: string;
}

type SharePermission = 'view' | 'view+edit';

type WorkingShare =
  | { granteeType: 'role'; granteeRoleId: number; permission: SharePermission }
  | { granteeType: 'user'; granteeUserId: number; permission: SharePermission };

function mapShareRecordToShare(record: OfferShareRecord): WorkingShare | null {
  if (record.granteeType === 'role') {
    // Skip malformed records where the expected ID is absent.
    if (record.granteeRoleId == null) return null;
    return {
      granteeRoleId: record.granteeRoleId,
      granteeType: 'role',
      permission: record.permission as SharePermission,
    };
  }

  // Skip malformed records where the expected ID is absent.
  if (record.granteeUserId == null) return null;
  return {
    granteeType: 'user',
    granteeUserId: record.granteeUserId,
    permission: record.permission as SharePermission,
  };
}

function mapWorkingShareToApiShare(share: WorkingShare) {
  return OfferShareSchema.parse(share);
}

function getShareLabel(
  share: WorkingShare,
  shareableUsers: ShareableUser[],
  shareableRoles: ShareableRole[],
  t: ReturnType<typeof useTranslation<'offers'>>['t'],
) {
  if (share.granteeType === 'role') {
    const role = shareableRoles.find((candidate) => candidate.id === share.granteeRoleId);
    return role?.name ?? t('workspace.header.sharing.roleFallback', { id: share.granteeRoleId });
  }

  const grantee = shareableUsers.find((candidate) => candidate.id === share.granteeUserId);
  return grantee?.name ?? t('workspace.header.sharing.userFallback', { id: share.granteeUserId });
}

function parsePositiveNumber(value: string): number | null {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) || parsed <= 0 ? null : parsed;
}

export function SharingDialog({ isOpen, offerId, onClose }: SharingDialogProps) {
  if (!isOpen) {
    return null;
  }

  return (
    <QueryBoundary
      fallback={<FormSkeleton />}
      renderFrame={(content) => (
        <SharingDialogFrame onClose={onClose}>{content}</SharingDialogFrame>
      )}
    >
      <SharingDialogContent offerId={offerId} onClose={onClose} />
    </QueryBoundary>
  );
}

function SharingDialogFrame({
  children,
  footer,
  onClose,
}: {
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
}) {
  const { t } = useTranslation('offers');

  return (
    <ModalDialog
      closeLabel={t('workspace.header.sharing.close')}
      footer={footer}
      header={<h2 className="text-base font-semibold">{t('workspace.header.sharing.title')}</h2>}
      maxWidth={SHARING_DIALOG_MAX_WIDTH}
      onClose={onClose}
      testId="offer-sharing-dialog"
    >
      {children}
    </ModalDialog>
  );
}

function SharingDialogContent({ offerId, onClose }: Omit<SharingDialogProps, 'isOpen'>) {
  const { t } = useTranslation('offers');
  const { shares, shareableUsers, shareableRoles } = useOfferSharingData(offerId);
  const updateOfferShares = useUpdateOfferShares(offerId);
  const [workingShares, setWorkingShares] = React.useState<WorkingShare[]>(() =>
    shares.map(mapShareRecordToShare).filter((s): s is WorkingShare => s !== null),
  );
  const [roleGrant, setRoleGrant] = React.useState<GrantFormState>({
    permission: 'view',
    value: '',
  });
  const [userGrant, setUserGrant] = React.useState<GrantFormState>({
    permission: 'view',
    value: '',
  });

  function addUserGrant() {
    const parsedUserId = parsePositiveNumber(userGrant.value);
    if (parsedUserId == null) return;

    setWorkingShares((current) => [
      ...current,
      { granteeType: 'user', granteeUserId: parsedUserId, permission: userGrant.permission },
    ]);
    setUserGrant({ permission: 'view', value: '' });
  }

  function addRoleGrant() {
    const parsedRoleId = parsePositiveNumber(roleGrant.value);
    if (parsedRoleId == null) return;

    setWorkingShares((current) => [
      ...current,
      { granteeRoleId: parsedRoleId, granteeType: 'role', permission: roleGrant.permission },
    ]);
    setRoleGrant({ permission: 'view', value: '' });
  }

  function removeShare(index: number) {
    setWorkingShares((current) => current.filter((_, shareIndex) => shareIndex !== index));
  }

  async function handleSave() {
    try {
      await updateOfferShares.mutateAsync({ shares: workingShares.map(mapWorkingShareToApiShare) });
      onClose();
    } catch {
      toast.error(t('workspace.header.sharing.toasts.saveError'));
    }
  }

  const userOptions = shareableUsers.map((candidate) => ({
    value: String(candidate.id),
    label: candidate.name,
  }));
  const roleOptions = shareableRoles.map((candidate) => ({
    value: String(candidate.id),
    label: candidate.name,
  }));

  return (
    <SharingDialogFrame
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('workspace.header.sharing.actions.cancel')}
          </Button>
          <Button
            aria-label={t('workspace.header.sharing.actions.save')}
            disabled={updateOfferShares.isPending}
            type="button"
            onClick={() => void handleSave()}
          >
            {t('workspace.header.sharing.actions.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-3 rounded-lg border border-border/60 p-4">
            <FormField label={t('workspace.header.sharing.fields.user')} testId="share-user">
              <SearchableSelectField
                aria-label={t('workspace.header.sharing.fields.user')}
                data-testid="share-user-select"
                options={userOptions}
                placeholder={t('workspace.header.sharing.fields.userPlaceholder')}
                value={userGrant.value}
                onChange={(value) => setUserGrant((current) => ({ ...current, value }))}
              />
            </FormField>
            <FormField
              label={t('workspace.header.sharing.fields.userPermission')}
              testId="share-user-permission"
            >
              <SearchableSelectField
                data-testid="share-user-permission-select"
                options={[
                  { value: 'view', label: t('workspace.header.sharing.permissions.view') },
                  { value: 'view+edit', label: t('workspace.header.sharing.permissions.viewEdit') },
                ]}
                value={userGrant.permission}
                onChange={(value) =>
                  setUserGrant((current) => ({
                    ...current,
                    permission: value as SharePermission,
                  }))
                }
              />
            </FormField>
            <Button type="button" variant="outline" onClick={addUserGrant}>
              {t('workspace.header.sharing.actions.addUser')}
            </Button>
          </div>

          <div className="space-y-3 rounded-lg border border-border/60 p-4">
            <FormField label={t('workspace.header.sharing.fields.role')} testId="share-role">
              <SearchableSelectField
                aria-label={t('workspace.header.sharing.fields.role')}
                data-testid="share-role-select"
                options={roleOptions}
                placeholder={t('workspace.header.sharing.fields.rolePlaceholder')}
                value={roleGrant.value}
                onChange={(value) => setRoleGrant((current) => ({ ...current, value }))}
              />
            </FormField>
            <FormField
              label={t('workspace.header.sharing.fields.rolePermission')}
              testId="share-role-permission"
            >
              <SearchableSelectField
                data-testid="share-role-permission-select"
                options={[
                  { value: 'view', label: t('workspace.header.sharing.permissions.view') },
                  { value: 'view+edit', label: t('workspace.header.sharing.permissions.viewEdit') },
                ]}
                value={roleGrant.permission}
                onChange={(value) =>
                  setRoleGrant((current) => ({
                    ...current,
                    permission: value as SharePermission,
                  }))
                }
              />
            </FormField>
            <Button type="button" variant="outline" onClick={addRoleGrant}>
              {t('workspace.header.sharing.actions.addRole')}
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-medium">{t('workspace.header.sharing.currentShares')}</h3>
          {workingShares.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('workspace.header.sharing.empty')}</p>
          ) : (
            <ul className="space-y-2">
              {workingShares.map((share, index) => (
                <li
                  key={
                    share.granteeType === 'role'
                      ? `role-${share.granteeRoleId}-${share.permission}`
                      : `user-${share.granteeUserId}-${share.permission}`
                  }
                  className="flex items-center justify-between gap-3 rounded-lg border border-border/60 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {getShareLabel(share, shareableUsers, shareableRoles, t)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {share.permission === 'view'
                        ? t('workspace.header.sharing.permissions.view')
                        : t('workspace.header.sharing.permissions.viewEdit')}
                    </p>
                  </div>
                  <Button
                    aria-label={t('workspace.header.sharing.removeAriaLabel', {
                      id: share.granteeType === 'role' ? share.granteeRoleId : share.granteeUserId,
                    })}
                    size="sm"
                    type="button"
                    variant="ghost"
                    onClick={() => removeShare(index)}
                  >
                    {t('workspace.header.sharing.actions.remove')}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </SharingDialogFrame>
  );
}
